// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui
import { Stack } from '@mui/material';

// api
import { useGetDeviceByIdQuery } from '@/api/recalc/endpoints/devicesApiSlice';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import LinearLoadingIndicator from '@/components/ui/progress/LinearLoadingIndicator';

// feature-components
import DeviceCapabilityCards from '@/features/devices/components/DeviceCapabilityCards';
import DeviceDetailHeader from '@/features/devices/components/DeviceDetailHeader';
import DeviceGeneralInfoDrawer from '@/features/devices/components/DeviceGeneralInfoDrawer';
import DeviceImageCard from '@/features/devices/components/DeviceImageCard';
import DeviceLocationCard from '@/features/devices/components/DeviceLocationCard';

// redux
import { skipToken } from '@reduxjs/toolkit/query';

export const Route = createFileRoute('/$accountId/_admin/devices/€deviceId/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('units-gateways-sensors');
  const { accountId, deviceId } = useParams({ strict: false });
  const [isEditGeneralInfoOpen, setIsEditGeneralInfoOpen] = useState(false);

  const parsedAccountId = accountId ? Number(accountId) : 0;
  const parsedDeviceId = deviceId ? Number(deviceId) : undefined;
  const queryDeviceId =
    typeof parsedDeviceId === 'number' && Number.isFinite(parsedDeviceId)
      ? parsedDeviceId
      : skipToken;

  const { data: device, isLoading } = useGetDeviceByIdQuery(
    queryDeviceId === skipToken
      ? skipToken
      : {
          deviceId: queryDeviceId,
          includeFields: ['MappedUnits'],
        },
  );

  return (
    <>
      <LinearLoadingIndicator isLoading={isLoading} />
      <PageContainer maxWidth="xl" sx={{ py: 2 }}>
        <PageHeader title={t('devices.detailTitle')} disableArrow={false} />

        {device && (
          <Stack spacing={2}>
            <DeviceDetailHeader
              accountId={parsedAccountId}
              device={device}
              onEditGeneralInformation={() => setIsEditGeneralInfoOpen(true)}
            />
            <DeviceImageCard device={device} />
            <DeviceLocationCard
              displayName={device.displayName}
              location={device.location}
            />
            <DeviceCapabilityCards
              deviceId={device.deviceId}
              capabilities={device.capabilities}
            />

            <DeviceGeneralInfoDrawer
              open={isEditGeneralInfoOpen}
              device={device}
              onClose={() => setIsEditGeneralInfoOpen(false)}
            />
          </Stack>
        )}
      </PageContainer>
    </>
  );
}
